import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { apiMethods } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import Button from '../components/Button'

function UserEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = !id || id === 'new'
  const { isSuperAdmin } = useAuthStore()

  const { data: user, isLoading: loadingUser } = useQuery({
    queryKey: ['user', id],
    queryFn: () => apiMethods.getUser(id),
    enabled: !!id && !isNew,
  })

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiMethods.getRoles(),
  })

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
  })

  useEffect(() => {
    if (user?.data) {
      setFormData({
        name: user.data.name || '',
        email: user.data.email || '',
        password: '',
        role: user.data.roles?.[0]?.name || '',
      })
    }
  }, [user])

  const updateMutation = useMutation({
    mutationFn: (data) =>
      isNew ? apiMethods.createUser(data) : apiMethods.updateUser(id, data),
    onSuccess: () => navigate('/users'),
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    updateMutation.mutate({
      name: formData.name,
      email: formData.email,
      password: formData.password || undefined,
      role: formData.role,
    })
  }

  if (loadingUser && !isNew) {
    return <div className="p-6">Loading...</div>
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">
        {isNew ? 'Create User' : 'Edit User'}
      </h1>

      {!isNew && !user ? (
        <div>User not found</div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password {!isNew && '(leave blank to keep current)'}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required={isNew}
                minLength={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {isSuperAdmin() && roles && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  name="role"
                  required
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                >
                  {roles.data.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <Button type="submit" disabled={updateMutation.isLoading}>
                {updateMutation.isLoading ? 'Saving...' : 'Save'}
              </Button>
              <button
                type="button"
                onClick={() => navigate('/users')}
                className="px-4 py-2 border border-gray-300 rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}

export default UserEdit
